// =============================================================================
// GENERATED by scripts/build-shared.cjs from functions/src/shared/costAllocationSplit.js
// DO NOT EDIT — edit the canonical file and re-run `node scripts/build-shared.cjs`.
// (TASK-9493: ONE split-receipt rule — validate, cents rounding, per-line
// attribution — client + server.)
//
// Side-effect module: attaches `window.CostSplit`. Every reader that attributes
// a costEntry to a job / vehicle / customer / allocation loops over
// `window.CostSplit.expandCostEntry(c)`: an unsplit entry comes back as `[c]`
// (the same object — parity by identity), a split one as one view per line.
//
// The body below is the canonical file's PORTABLE CORE, copied byte for byte.
// =============================================================================
(function () {
  'use strict';

  /** Hard cap on split lines — a receipt, not a ledger. */
  var COST_SPLIT_MAX_LINES = 20;
  /** Max length of any id carried on a line. */
  var COST_SPLIT_ID_MAX = 100;
  /** Label fields a line may carry for display (server re-derives them). */
  var COST_SPLIT_LABEL_KEYS = ['costAllocationLabel', 'jobName', 'vehicleDisplay', 'customerId', 'customerName'];

  /**
   * Dollars -> integer cents, round-half-away-from-zero on the DECIMAL value.
   * Goes through toFixed(6) first so binary noise (0.1 + 0.2, 1.005 stored as
   * 1.00499999...) rounds the way a human reading the number expects.
   * Returns NaN for anything that is not a finite number.
   */
  function toCents(v) {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean') return NaN;
    var n = Number(v);
    if (!isFinite(n)) return NaN;
    var fixed = Number(Math.abs(n).toFixed(6));
    var c = Math.round(fixed * 100 + 1e-7);
    return n < 0 ? -c : c;
  }

  /** Integer cents -> dollars (a plain number with at most 2 decimals). */
  function centsToDollars(c) {
    return Math.round(c) / 100;
  }

  function _splitId(v) {
    if (v === null || v === undefined) return null;
    if (typeof v !== 'string' && typeof v !== 'number') return null;
    var s = String(v).trim();
    if (!s) return null;
    return s.slice(0, COST_SPLIT_ID_MAX);
  }

  function _splitLabel(v, max) {
    if (v === null || v === undefined) return null;
    if (typeof v !== 'string' && typeof v !== 'number') return null;
    var s = String(v).trim();
    return s ? s.slice(0, max || 200) : null;
  }

  /**
   * Validate + normalize a proposed split against its parent amount.
   *
   * @param {number} parentAmount  the costEntry's full amount (dollars)
   * @param {*} allocations        the proposed allocations array
   * @returns {{ ok: boolean, split: boolean, lines: Array|null, code?: string, error?: string,
   *             parentCents?: number, sumCents?: number, diffCents?: number }}
   *   - absent / null / [] -> { ok: true, split: false, lines: null }  (unsplit)
   *   - valid              -> { ok: true, split: true, lines: [...normalized] }
   *   - anything else      -> { ok: false, code, error }  (NEVER silently repaired)
   */
  function validateCostAllocations(parentAmount, allocations) {
    if (allocations === undefined || allocations === null) return { ok: true, split: false, lines: null };
    if (!Array.isArray(allocations)) return { ok: false, code: 'not_array', error: 'Split lines must be a list.' };
    if (allocations.length === 0) return { ok: true, split: false, lines: null };
    if (allocations.length < 2) return { ok: false, code: 'too_few', error: 'A split needs at least two lines.' };
    if (allocations.length > COST_SPLIT_MAX_LINES) {
      return { ok: false, code: 'too_many', error: 'A split can have at most ' + COST_SPLIT_MAX_LINES + ' lines.' };
    }
    var parentCents = toCents(parentAmount);
    // A refund / vendor credit is a NEGATIVE costEntry and may carry a split that
    // mirrors the original's (vendorCredits.js prorates it). Every line must then
    // carry the parent's sign — a split can move money between lines, never flip it.
    if (!isFinite(parentCents) || parentCents === 0) {
      return { ok: false, code: 'bad_parent', error: 'Enter the receipt total before splitting it.' };
    }
    var lines = [];
    var sum = 0;
    for (var i = 0; i < allocations.length; i++) {
      var a = allocations[i];
      if (!a || typeof a !== 'object' || Array.isArray(a)) {
        return { ok: false, code: 'bad_line', error: 'Split line ' + (i + 1) + ' is not valid.' };
      }
      var cents = toCents(a.amount);
      if (!isFinite(cents) || cents === 0 || (cents > 0) !== (parentCents > 0)) {
        return { ok: false, code: 'bad_amount', error: 'Split line ' + (i + 1) + ' needs an amount greater than $0.' };
      }
      var line = {
        costAllocationId: _splitId(a.costAllocationId),
        jobId: _splitId(a.jobId),
        vehicleId: _splitId(a.vehicleId),
        amount: centsToDollars(cents),
      };
      // TASK-10162 S4 — a work order belongs to ONE line (only when sent).
      var st = _splitId(a.sourceTaskId);
      if (st) line.sourceTaskId = st;
      if (!line.costAllocationId && !line.jobId && !line.vehicleId) {
        return { ok: false, code: 'unattributed', error: 'Split line ' + (i + 1) + ' needs a cost allocation, job or vehicle.' };
      }
      for (var k = 0; k < COST_SPLIT_LABEL_KEYS.length; k++) {
        var key = COST_SPLIT_LABEL_KEYS[k];
        line[key] = key === 'customerId' ? _splitId(a[key]) : _splitLabel(a[key], 200);
      }
      sum += cents;
      lines.push(line);
    }
    if (sum !== parentCents) {
      var diff = parentCents - sum;
      return {
        ok: false, code: 'sum_mismatch',
        error: 'Split lines add up to ' + _fmtCents(sum) + ' but the receipt is ' + _fmtCents(parentCents)
          + ' (' + (diff > 0 ? _fmtCents(diff) + ' left to allocate' : _fmtCents(-diff) + ' over') + ').',
        parentCents: parentCents, sumCents: sum, diffCents: diff,
      };
    }
    return { ok: true, split: true, lines: lines, parentCents: parentCents, sumCents: sum, diffCents: 0 };
  }

  function _fmtCents(c) {
    var neg = c < 0;
    var abs = Math.abs(Math.round(c));
    var d = Math.floor(abs / 100);
    var r = abs % 100;
    return (neg ? '-' : '') + '$' + String(d).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + (r < 10 ? '0' : '') + r;
  }

  /**
   * The read-time verdict on a stored entry.
   *   'none'    — no allocations; reads exactly as it always did.
   *   'split'   — a balanced split; readers expand it per line.
   *   'invalid' — allocations are present but do not balance against the parent
   *               (e.g. the amount was edited afterwards). Read as UNSPLIT so the
   *               money never double-counts; surfaces can flag it.
   */
  function splitStatus(c) {
    if (!c || c.allocations === undefined || c.allocations === null) return 'none';
    if (Array.isArray(c.allocations) && c.allocations.length === 0) return 'none';
    var v = validateCostAllocations(c.amount, c.allocations);
    return v.ok && v.split ? 'split' : 'invalid';
  }

  /** True when the entry carries a balanced split. */
  function isSplitCostEntry(c) {
    return splitStatus(c) === 'split';
  }

  /**
   * The effective attribution lines of an entry — ALWAYS at least one line.
   *   split   -> the normalized lines (amount per line)
   *   unsplit -> ONE line built from the parent's single fields, amount = c.amount
   *              exactly as stored (no rounding).
   */
  function getEffectiveCostAllocations(c) {
    if (!c) return [];
    var v = (c.allocations === undefined || c.allocations === null) ? null : validateCostAllocations(c.amount, c.allocations);
    if (v && v.ok && v.split) return v.lines;
    return [{
      costAllocationId: c.costAllocationId || null,
      jobId: c.jobId || null,
      vehicleId: c.vehicleId || c.truckId || null,
      amount: c.amount,
      costAllocationLabel: c.costAllocationLabel || null,
      jobName: c.jobName || null,
      vehicleDisplay: c.vehicleDisplay || null,
      customerId: c.customerId || null,
      customerName: c.customerName || null,
    }];
  }

  /**
   * Expand an entry into per-attribution VIEWS for any reader that buckets cost
   * by job / vehicle / customer / allocation.
   *   unsplit (or invalid split) -> [c]   (the same object — parity by identity)
   *   split                      -> one shallow clone per line, attribution
   *                                 fields REPLACED from the line, amount = line.
   * Clones carry `_splitOf` (parent id), `_splitIndex`, `_splitShare` (0..1).
   */
  function expandCostEntry(c) {
    if (!c) return [];
    if (c.allocations === undefined || c.allocations === null) return [c];
    var v = validateCostAllocations(c.amount, c.allocations);
    if (!v.ok || !v.split) return [c];
    var out = [];
    for (var i = 0; i < v.lines.length; i++) {
      var l = v.lines[i];
      var view = {};
      for (var k in c) { if (Object.prototype.hasOwnProperty.call(c, k)) view[k] = c[k]; }
      var share = toCents(l.amount) / v.parentCents;
      view.amount = l.amount;
      view.jobId = l.jobId;
      view.jobName = l.jobName;
      view.customerId = l.customerId;
      view.customerName = l.customerName;
      view.vehicleId = l.vehicleId;
      view.truckId = l.vehicleId;
      // Other per-entity keys some readers fall back on (bi.js getEntityCostInRange)
      // must not leak the parent's value onto a line that names a different unit.
      if ('trailerId' in view) view.trailerId = null;
      if ('assetId' in view) view.assetId = null;
      view.vehicleDisplay = l.vehicleDisplay;
      view.costAllocationId = l.costAllocationId;
      view.costAllocationLabel = l.costAllocationLabel;
      view.sourceTaskId = l.sourceTaskId || null; // never the parent's (TASK-10162 S4)
      if (typeof c.fuelGallons === 'number' && isFinite(c.fuelGallons)) {
        view.fuelGallons = Math.round(c.fuelGallons * share * 1000) / 1000;
      }
      view._splitOf = c.id || null;
      view._splitIndex = i;
      view._splitShare = share;
      out.push(view);
    }
    return out;
  }

  /** flatMap(expandCostEntry) over a list — the one-liner most readers need. */
  function expandCostEntries(list) {
    var out = [];
    if (!list || typeof list.length !== 'number') return out;
    for (var i = 0; i < list.length; i++) {
      var views = expandCostEntry(list[i]);
      for (var j = 0; j < views.length; j++) out.push(views[j]);
    }
    return out;
  }

  /**
   * Even split helper for the UI: `n` lines of integer cents summing EXACTLY to
   * totalCents. The leftover cents go to the FIRST lines, one each.
   */
  function evenSplitCents(totalCents, n) {
    if (!(n >= 1) || !isFinite(totalCents) || totalCents < 0) return [];
    n = Math.floor(n);
    var base = Math.floor(totalCents / n);
    var rem = totalCents - base * n;
    var out = [];
    for (var i = 0; i < n; i++) out.push(base + (i < rem ? 1 : 0));
    return out;
  }

  /**
   * Re-spread a balanced split over a NEW total (e.g. a partial vendor credit
   * against a split expense), in proportion to each line's share. Largest-
   * remainder in integer cents, so the result sums EXACTLY to newTotal; ties go
   * to the earlier line. Lines that would round to 0 cents are dropped (a line
   * must carry money). Returns null when `lines` is not a balanced split.
   * The result may be ONE line (a tiny credit whose other shares round to 0
   * cents): that is not a split, so the caller attributes the whole credit to
   * that line's job / vehicle / allocation instead (COD, TASK-9493).
   */
  function prorateCostAllocations(parentAmount, lines, newTotal) {
    var v = validateCostAllocations(parentAmount, lines);
    if (!v.ok || !v.split) return null;
    var target = toCents(newTotal);
    if (!isFinite(target) || target === 0) return null;
    var absTarget = Math.abs(target);
    var absParent = Math.abs(v.parentCents);
    var sign = target < 0 ? -1 : 1;
    var parts = [];
    var used = 0;
    for (var i = 0; i < v.lines.length; i++) {
      var exact = Math.abs(toCents(v.lines[i].amount)) * absTarget / absParent;
      var fl = Math.floor(exact + 1e-9);
      parts.push({ i: i, c: fl, r: exact - fl });
      used += fl;
    }
    var left = absTarget - used;
    var order = parts.slice().sort(function (a, b) { return (b.r - a.r) || (a.i - b.i); });
    for (var k = 0; k < order.length && left > 0; k++) { order[k].c += 1; left--; }
    var out = [];
    for (var j = 0; j < parts.length; j++) {
      if (parts[j].c <= 0) continue;
      var src = v.lines[parts[j].i];
      var line = {};
      for (var key in src) { if (Object.prototype.hasOwnProperty.call(src, key)) line[key] = src[key]; }
      line.amount = centsToDollars(sign * parts[j].c);
      out.push(line);
    }
    return out.length >= 1 ? out : null;
  }

  /** Cents still to allocate (positive = left, negative = over). Blank lines count 0. */
  function remainingCents(parentAmount, lineAmounts) {
    var p = toCents(parentAmount);
    if (!isFinite(p)) p = 0;
    var s = 0;
    for (var i = 0; i < (lineAmounts || []).length; i++) {
      var c = toCents(lineAmounts[i]);
      if (isFinite(c)) s += c;
    }
    return p - s;
  }

  window.CostSplit = Object.freeze({
    MAX_LINES: COST_SPLIT_MAX_LINES,
    toCents: toCents,
    centsToDollars: centsToDollars,
    formatCents: _fmtCents,
    validateCostAllocations: validateCostAllocations,
    splitStatus: splitStatus,
    isSplitCostEntry: isSplitCostEntry,
    getEffectiveCostAllocations: getEffectiveCostAllocations,
    expandCostEntry: expandCostEntry,
    expandCostEntries: expandCostEntries,
    evenSplitCents: evenSplitCents,
    prorateCostAllocations: prorateCostAllocations,
    remainingCents: remainingCents
  });
})();
